import {ItemPriceBreakdown, MultiCurrencyPrice} from '@wix/auto_sdk_ecom_cart-v-2';
import {SPECS} from '../../../../common/constants';
import {MultiCurrencyPriceFragment} from '../../../../gql/graphql';
import {ModelContext} from '../../../../types/checkoutApp.types';
import {PriceModel} from '../Price.model';

export class BreakdownModel {
  public comparePrice?: PriceModel;
  public comparePriceByQuantity?: PriceModel;
  public basePrice?: PriceModel;
  public totalModifiersPrice?: PriceModel;
  public isLongPrice: boolean;

  constructor(params: BreakdownModel) {
    this.comparePrice = params.comparePrice;
    this.comparePriceByQuantity = params.comparePriceByQuantity;
    this.basePrice = params.basePrice;
    this.totalModifiersPrice = params.totalModifiersPrice;
    this.isLongPrice = params.isLongPrice;
  }

  public static fromCheckoutV1({
    unitPrice,
    fullPrice,
    salePrice,
    totalModifiersPrice,
    confirmedQuantity,
    modeledTotalPrice,
    modelContext,
  }: {
    unitPrice?: MultiCurrencyPriceFragment | null;
    fullPrice?: MultiCurrencyPriceFragment | null;
    salePrice?: MultiCurrencyPriceFragment | null;
    totalModifiersPrice?: MultiCurrencyPriceFragment | null;
    confirmedQuantity: number;
    modeledTotalPrice: PriceModel;
    modelContext: ModelContext;
  }) {
    const hasSale = fullPrice?.formattedAmount !== unitPrice?.formattedAmount;
    const hasAutomaticDiscount = unitPrice?.formattedAmount !== salePrice?.formattedAmount;
    const modeledComparePrice = hasSale
      ? PriceModel.fromCheckoutV1(hasAutomaticDiscount ? salePrice : fullPrice, modelContext)
      : undefined;
    const modeledTotalModifiersPrice = PriceModel.fromCheckoutV1(totalModifiersPrice, modelContext);
    const modeledUnitPrice = PriceModel.fromCheckoutV1(unitPrice, modelContext);

    return new BreakdownModel({
      comparePrice: modeledComparePrice,
      comparePriceByQuantity: modelContext.flowAPI.experiments.enabled(SPECS.ClientFormattingForPrices)
        ? PriceModel.fromCheckoutV1(
            crossedOutPriceByQuantity({
              modeledComparePrice,
              confirmedQuantity,
              modelContext,
            }),
            modelContext
          )
        : undefined,
      basePrice: PriceModel.fromCheckoutV1(
        getBaseUnitPrice({modeledTotalModifiersPrice, modeledUnitPrice, modeledComparePrice, modelContext}),
        modelContext
      ),
      totalModifiersPrice: modeledTotalModifiersPrice,
      isLongPrice:
        modeledTotalPrice.formattedAmount.length > 12 || (hasSale && modeledComparePrice!.formattedAmount.length > 12),
    });
  }
  /* istanbul ignore next */
  public static fromCartV2({
    breakdown,
    unitPrice,
    modeledTotalPrice,
    confirmedQuantity,
    modelContext,
  }: {
    breakdown: ItemPriceBreakdown;
    unitPrice: MultiCurrencyPrice;
    modeledTotalPrice: PriceModel;
    confirmedQuantity: number;
    modelContext: ModelContext;
  }): BreakdownModel {
    const hasSale = breakdown.fullPrice?.formattedAmount !== unitPrice?.formattedAmount;
    const hasAutomaticDiscount = unitPrice?.formattedAmount !== breakdown.salePrice?.formattedAmount;
    const modeledComparePrice = hasSale
      ? PriceModel.fromCartV2(hasAutomaticDiscount ? breakdown.salePrice : breakdown.fullPrice, modelContext)
      : undefined;
    const modeledTotalModifiersPrice = PriceModel.fromCartV2(breakdown.totalModifiersPrice, modelContext);
    const modeledUnitPrice = PriceModel.fromCartV2(unitPrice, modelContext);

    return new BreakdownModel({
      comparePrice: modeledComparePrice,
      comparePriceByQuantity: modelContext.flowAPI.experiments.enabled(SPECS.ClientFormattingForPrices)
        ? PriceModel.fromCartV2(
            crossedOutPriceByQuantity({
              modeledComparePrice,
              confirmedQuantity,
              modelContext,
            }),
            modelContext
          )
        : undefined,
      basePrice: PriceModel.fromCartV2(
        getBaseUnitPrice({modeledTotalModifiersPrice, modeledUnitPrice, modeledComparePrice, modelContext}),
        modelContext
      ),
      totalModifiersPrice: PriceModel.fromCartV2(breakdown.totalModifiersPrice, modelContext),
      isLongPrice:
        modeledTotalPrice.formattedAmount.length > 12 || (hasSale && modeledComparePrice!.formattedAmount.length > 12),
    });
  }
}

function crossedOutPriceByQuantity({
  modeledComparePrice,
  confirmedQuantity,
  modelContext,
}: {
  modeledComparePrice?: PriceModel;
  confirmedQuantity: number;
  modelContext: ModelContext;
}) {
  if (!modeledComparePrice?.amount) {
    return undefined;
  }
  const totalPriceByQuantity = modeledComparePrice?.amount * confirmedQuantity;
  return {
    amount: totalPriceByQuantity.toString(),
    convertedAmount: totalPriceByQuantity.toString(),
    formattedAmount: modelContext.formatAmount(totalPriceByQuantity),
    formattedConvertedAmount: modelContext.formatAmount(totalPriceByQuantity),
  };
}

function getBaseUnitPrice({
  modeledTotalModifiersPrice,
  modeledUnitPrice,
  modeledComparePrice,
  modelContext,
}: {
  modeledTotalModifiersPrice?: PriceModel;
  modeledUnitPrice: PriceModel;
  modeledComparePrice?: PriceModel;
  modelContext: ModelContext;
}) {
  const modifiersTotalPriceAmount = Number(modeledTotalModifiersPrice?.amount);
  const unitPriceAmount = modeledComparePrice?.amount ? modeledComparePrice.amount : modeledUnitPrice.amount;
  const baseUnitPriceAmount = unitPriceAmount - modifiersTotalPriceAmount;

  return {
    amount: baseUnitPriceAmount.toString(),
    convertedAmount: baseUnitPriceAmount.toString(),
    formattedAmount: modelContext.formatAmount(baseUnitPriceAmount),
    formattedConvertedAmount: modelContext.formatAmount(baseUnitPriceAmount),
  };
}
